import React, { useState, useContext, useEffect, useRef } from 'react';
import Axios from 'axios';
import { ViewContext } from 'components/lib';
import Style from './aiagent.module.scss';

const SUGGESTIONS = [
  '¿Cuántos niños fueron transportados esta semana?',
  '¿Qué rutas están activas hoy?',
  '¿Cuáles clientes tienen pagos pendientes?',
  '¿Cuántas sesiones se completaron este mes?',
];

function parseMarkdown(text) {
  if (!text) return '';
  let t = text
    .replace(/^### (.*$)/gm, '<h3>$1</h3>')
    .replace(/^## (.*$)/gm,  '<h2>$1</h2>')
    .replace(/^# (.*$)/gm,   '<h1>$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g,    '<em>$1</em>')
    .replace(/^[*-] (.*)$/gm, '<li>$1</li>')
    .replace(/\n/g, '<br/>');
  return t;
}

const IcoSpark = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
  </svg>
);

const IcoSend = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/>
    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);

const IcoClose = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

export function AiAgent() {
  const context        = useContext(ViewContext);
  const [open,     setOpen]     = useState(false);
  const [messages, setMessages] = useState([]);
  const [input,    setInput]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  async function send(text) {
    const prompt = (text || input).trim();
    if (!prompt || loading) return;

    setMessages(prev => [...prev, { role: 'user', text: prompt }]);
    setInput('');
    setLoading(true);

    const aiBase = process.env.NODE_ENV === 'development'
      ? 'http://localhost:3004'
      : 'https://apicf.kaytrek.com';

    try {
      const { data } = await Axios.post(`${aiBase}/api/demo/gptrcf`, { prompt, detailed: false });
      const inner = data?.data;
      const reply = inner?.naturalResponse || inner?.response || (typeof inner === 'string' ? inner : null)
        || 'No pude generar una respuesta. Intenta de nuevo.';
      setMessages(prev => [...prev, { role: 'bot', text: reply }]);
    } catch (err) {
      const isNetwork = !err.response;
      const reply = isNetwork
        ? 'El asistente no está disponible en este momento. Verifica que el servicio IA esté activo.'
        : `Error ${err.response?.status}: ${err.response?.data?.message || 'No se pudo procesar la consulta.'}`;
      setMessages(prev => [...prev, { role: 'bot', text: reply, error: true }]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {/* ── Trigger ── */}
      {!open && (
        <button className={Style.trigger} onClick={() => setOpen(true)} aria-label="Asistente IA">
          <IcoSpark />
        </button>
      )}

      {/* ── Chat window ── */}
      {open && (
        <div className={Style.window}>

          {/* Header */}
          <div className={Style.header}>
            <div className={Style.headerLeft}>
              <div className={Style.headerIcon}><IcoSpark /></div>
              <div>
                <div className={Style.headerTitle}>Asistente IA</div>
                <div className={Style.headerSub}>Consulta datos del sistema</div>
              </div>
            </div>
            <button className={Style.closeBtn} onClick={() => setOpen(false)} aria-label="Cerrar">
              <IcoClose />
            </button>
          </div>

          {/* Messages */}
          <div className={Style.messages}>
            {messages.length === 0 && (
              <div className={Style.empty}>
                <div className={Style.emptyIcon}><IcoSpark /></div>
                <p className={Style.emptyText}>¿En qué te puedo ayudar?</p>
                <div className={Style.suggestions}>
                  {SUGGESTIONS.map(s => (
                    <button key={s} className={Style.chip} onClick={() => send(s)}>{s}</button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`${Style.bubble} ${m.role === 'user' ? Style.bubbleUser : Style.bubbleBot} ${m.error ? Style.bubbleError : ''}`}>
                <div
                  className={Style.bubbleText}
                  dangerouslySetInnerHTML={{ __html: parseMarkdown(m.text) }}
                />
              </div>
            ))}

            {loading && (
              <div className={`${Style.bubble} ${Style.bubbleBot}`}>
                <div className={Style.dots}>
                  <span/><span/><span/>
                </div>
              </div>
            )}

            <div ref={endRef} />
          </div>

          {/* Input */}
          <div className={Style.inputRow}>
            <input
              className={Style.input}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Escribe tu pregunta..."
              disabled={loading}
            />
            <button
              className={Style.sendBtn}
              onClick={() => send()}
              disabled={loading || !input.trim()}
              aria-label="Enviar"
            >
              <IcoSend />
            </button>
          </div>

        </div>
      )}
    </>
  );
}
